import moment from 'moment';

export function isEmptyObj(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
}

// check if object is empty
export function isEmpty(val) {
  return !val || val.length === 0 || val === '' || isEmptyObj(val);
}

/**
 * Verifies the 'actual' value contains all the 'expected' values
 * @param expected {array} the expected value
 * @param actual {array} the actual value
 * @returns {*|boolean}
 */
export function isInclusive(expected, actual) {
  return expected.every(val => actual.indexOf(val) > -1);
}

// simple range from 0 to i
export function range(i) {
  return Array.from(Array(i).keys());
}

export function flattenArray(arr) {
  return arr.reduce(
    (a, b) => a.concat(Array.isArray(b) ? flattenArray(b) : b),
    []
  );
}

export function calcAvgDishPrice(dishes = []) {
  if (dishes.length < 1) return 0;
  return Math.round(
    dishes.map(dish => dish.price_cents).reduce((a, b) => a + b, 0) /
      dishes.length
  );
}

/**
 * Formats cents as a dollar amount
 * @param cents
 * @returns {string}
 */
export function formatPrice(cents) {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}

/**
 * Converts a formatted string ($1.00) to an int (100)
 * @param string
 * @returns {number}
 */
export function dollarsToCents(string) {
  if (typeof string !== 'string') return string;
  // return string.replace(/[^0-9]/g, '');
  return Number.parseInt(string.replace(/[^0-9]/g, ''), 10);
}

/**
 * Parses filters for search.
 * @param dirtyFilters
 * @returns {{}}
 */
export const parseFilters = dirtyFilters => {
  const filters = {};
  dirtyFilters.forEach(filter => {
    if (filter.type in filters) filters[filter.type].push(filter.id);
    else {
      filters[filter.type] = [filter.id];
    }
  });
  return filters;
};

/**
 * Removes all (nested included) properties named 'id' from an object,
 * @param thingy
 * @returns {*}
 */
export const purgeIDs = thingy => {
  if (Array.isArray(thingy)) {
    thingy = thingy.map(thing => purgeIDs(thing));
  }
  if (isObject(thingy)) {
    const objects = Object.entries(thingy);
    objects.forEach(([key, value]) => {
      if (key === 'id') thingy[key] = null;

      // if it's an array or an object, go deeper
      if (Array.isArray(value) || isObject(value))
        thingy[key] = purgeIDs(value);
    });
  }
  return thingy;
};

/**
 * Shallow check if an object
 * @param maybeObject
 * @returns {boolean}
 */
export const isObject = obj => {
  return obj !== null && typeof obj === 'object';
};

export const safeRender = (prop, obj) => {
  if (isObject(obj) && prop in obj) return obj[prop] || '';
  return '';
};

export const stringfyServerErrors = dataObj => {
  const { message: err } = dataObj;

  if (dataObj && err) {
    const error = JSON.parse(err);
    const { errors, messages = [] } = error;

    if (messages.length > 0) return messages.join(', ');

    if (!errors) {
      console.log(err);
      return 'Unknown Error';
    }

    if (typeof errors === 'string') return errors;

    if (Array.isArray(errors) && errors.every(er => typeof er === 'string'))
      return errors.join(', ');

    const errorKeys = Object.keys(errors);
    return errorKeys
      .map(
        key =>
          `${key.charAt(0).toUpperCase() + key.slice(1)} ${errors[key].join(
            ', '
          )}`
      )
      .join(', ');
  }
};

export const calcTax = (value, rate) => {
  return Math.ceil(value * rate);
};

export const ignoreEnter = event => {
  if (event.which === 13) event.preventDefault();
};

/**
 * Throttles a function.
 * @param callback
 * @param wait
 * @param context
 * @returns {Function}
 */
export const throttle = (callback, wait = 200, context = this) => {
  let last;
  let deferTimer;

  return function() {
    let now = +new Date();
    let args = arguments;

    if (last && now < last + wait) {
      // preserve by debouncing the last call
      clearTimeout(deferTimer);
      deferTimer = setTimeout(function() {
        last = now;
        callback.apply(context, args);
      }, wait);
    } else {
      last = now;
      callback.apply(context, args);
    }
  };
};

/**
 * Debounces a function.
 * @param callback
 * @param wait
 * @param context
 * @returns {Function}
 */
export const debounce = (callback, wait = 200, context = this) => {
  let timeout = null;
  let callbackArgs = null;

  const later = () => callback.apply(context, callbackArgs);

  return () => {
    callbackArgs = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const mapSelectOptions = (options, labels) => {
  return options.map(option => ({ value: option, label: labels(option) }));
};

export const parseUrlParams = search =>
  decodeURIComponent(search)
    .substring(1)
    .split('&')
    .map(str => ({ [str.split('=')[0]]: str.split('=')[1] }))
    .reduce((ex, obj) => ({ ...obj, ...ex }));

export const formatStartAndEndTime = (starttime, endtime) => {
  return `${moment(starttime).format('h:mma')} - ${moment(endtime).format(
    'h:mma'
  )}`;
};

export const limitStringLength = (str, amt) => {
  return str.length > amt ? `${str.slice(0, amt)}...` : str;
};

export const isZipValid = str => {
  // Length is 5 and all number characters
  return str.length === 5 && str.match('^[0-9]+$');
};

export const isElementInViewport = el => {
  var rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Returns a function that can be used to make multiple API in a row and only return
 * most recent one. Any call made previously with the same 'key' will be thrown out.
 * @param callback
 * @param wait
 * @param context
 * @returns {Function}
 */

function buildGetLatestRequest() {
  let lastTime = {};
  return (key, apiCallPromise, success, error) => {
    const time = Date.now();
    lastTime[key] = time;
    apiCallPromise
      .then((result) => lastTime[key] === time && success(result))
      .catch(error);
  };
}

export const getLatestRequest = buildGetLatestRequest()



// WEBPACK FOOTER //
// ./src/helpers/util.js